import React, { useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  IconButton,
  MenuItem,
} from "@mui/material";
import Button from "../../design/Button";
import {
  CustomInput,
  CustomSelect,
} from "../../design/components/customComponents";
import { Close as CloseIcon } from "@mui/icons-material";
import { plusIcon } from "../../../utility-functions/assetLoader";
import POCEmailContainer from "./POCEmailContainer";
import { validateEmail } from "../../../utility-functions";
import colors from "../../design/colors";

const eventTypes = [
  { value: "call.status", label: "Triggered when call status changes" },
  {
    value: "call.recording",
    label: "Triggered when call recording is available",
  },
  {
    value: "call.insights",
    label: "Triggered when call insights are available",
  },
  {
    value: "call.propertyChange.notes",
    label: "Triggered when call notes change",
  },
  {
    value: "call.propertyChange.call_reason",
    label: "Triggered when call reason changes",
  },
  {
    value: "call.propertyChange.call_outcome",
    label: "Triggered when call outcome changes",
  },
  {
    value:"call.propertyChange.metadata",
    label:"Triggered when the call metadata associated with a call changes."
  },
  {
    value: "call.summary",
    label: "Triggered when call insights and status is available.",
  },
];

export default function CreateWebhook({
  redirectUrl,
  setRedirectUrl,
  pocEmails,
  setPocEmails,
  eventType,
  setEventType,
  headers,
  setHeaders,
  isRedirectUrlValid,
  setIsValid,
}) {
  //Handlers
  const handleChange = (index, field, newValue) => {
    const updated = [...headers];
    updated[index][field] = newValue;
    setHeaders(updated);
  };

  const handleAddHeader = () => {
    if (headers.length < 5) setHeaders([...headers, { key: "", value: "" }]);
  };

  const handleRemoveHeader = (index) => {
    const updated = [...headers];
    updated.splice(index, 1);
    setHeaders(updated);
  };

  const handleEmailChange = (e, index) => {
    const values = [...pocEmails];
    values[index] = e.target.value;
    setPocEmails(values);
  };

  const handleAddEmail = () => {
    if (pocEmails.length < 5) {
      setPocEmails([...pocEmails, ""]);
    }
  };

  const handleRemoveEmail = (index) => {
    const newEmails = [...pocEmails];
    newEmails.splice(index, 1);
    setPocEmails(newEmails);
  };

  const redirectUrlError =
    redirectUrl && !isRedirectUrlValid(redirectUrl) ? "Invalid URL format" : "";

  //Effects
  useEffect(() => {
    const allEmailsValid = pocEmails.every((email) => validateEmail(email));
    const valid =
      eventType &&
      isRedirectUrlValid(redirectUrl) &&
      headers.every((h) => h.key.trim() && h.value.trim()) &&
      allEmailsValid;
    setIsValid(valid);
  }, [redirectUrl, eventType, headers, pocEmails]);

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        maxWidth: "400px",
      }}
    >
      {/* EVENT TYPE */}
      <Box
        sx={{
          gap: "4px",
          width: "400px",
        }}
      >
        <CustomSelect
          label="Event type"
          handleChange={(e) => setEventType(e.target.value)}
          value={eventType}
          defaultValue="Select"
          selectStyles={{ marginTop: "4px" }}
          extraOptions={eventTypes.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={300}
                  color="text.primary"
                >
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        />
      </Box>

      {/* URL */}
      <Box
        sx={{
          gap: "4px",
          width: "400px",
        }}
      >
        <Typography
          variant="subtitle2"
          component="label"
          htmlFor="redirect-url"
          sx={{ mb: 0.5, display: "block", color: "text.secondary" }}
        >
          URL
        </Typography>

        <CustomInput
          name="redirectUrl"
          type="url"
          placeholder="https://example.com"
          value={{ value: redirectUrl, error: redirectUrlError }}
          handleChange={(e) => setRedirectUrl(e.target.value)}
          handleFocus={() => {}}
          style={{
            minWidth: "400px",
            height: "36px",
            borderRadius: "8px",
          }}
          inputContainerStyle={{
            width: "100%",
            gap: "8px",
          }}
          labelStyles={{ marginBottom: "0.5rem" }}
        />
      </Box>

      <POCEmailContainer
        pocEmails={pocEmails}
        handleAddEmail={handleAddEmail}
        handleRemoveEmail={handleRemoveEmail}
        handleEmailChange={handleEmailChange}
      />

      {/* HEADERS */}
      <Box
        sx={{
          gap: "4px",
          width: "400px",
        }}
      >
        <Typography variant="subtitle2" mb={1}>
          Custom header{" "}
          <Typography variant="caption" component="span" color="text.secondary">
            (optional)
          </Typography>
        </Typography>

        {headers.map((header, index) => (
          <Box
            key={index}
            display="flex"
            alignItems="center"
            sx={{
              width: "400px",
              gap: "6px",
              mb: 1,
              height: "36px",
            }}
          >
            {/* Key + Value fields */}
            <Box sx={{ display: "flex", gap: "6px", flex: 1 }}>
              <TextField
                placeholder="Key"
                value={header.key}
                onChange={(e) => handleChange(index, "key", e.target.value)}
                size="small"
                sx={{
                  width: "176px",
                  "& .MuiOutlinedInput-root": {
                    height: "36px",
                    padding: "0 12px",
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "#D0D5DD" },
                    "&:hover fieldset": { borderColor: colors.cgreen_600 },
                    "&.Mui-focused fieldset": { borderColor: colors.cgreen_600 },
                  },
                  "& input": {
                    padding: 0,
                    fontSize: "14px",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    lineHeight: "20px",
                  },
                }}
              />

              <TextField
                placeholder="Value"
                value={header.value}
                onChange={(e) => handleChange(index, "value", e.target.value)}
                size="small"
                sx={{
                  width: "176px",
                  "& .MuiOutlinedInput-root": {
                    height: "36px",
                    padding: "0 12px",
                    borderRadius: "8px",
                    "& fieldset": { borderColor: "#D0D5DD" },
                    "&:hover fieldset": { borderColor: colors.cgreen_600 },
                    "&.Mui-focused fieldset": { borderColor: colors.cgreen_600 },
                  },
                  "& input": {
                    padding: 0,
                    fontSize: "14px",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    lineHeight: "20px",
                  },
                }}
              />
            </Box>

            {/* Remove Button */}
            <IconButton
              onClick={() => handleRemoveHeader(index)}
              size="small"
              sx={{ height: "36px", width: "36px" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          buttonText="Add header"
          onClick={handleAddHeader}
          variant="outlined"
          hierarchy="white"
          disabled={headers.length >= 5}
          icon={{
            startIcon: plusIcon(headers?.length >= 5 ? colors.cgrey_300 : colors.cgreen_600),
          }}
          tooltipMessage={
            headers?.length >= 5 ? "Max 5 custom headers can be added " : ""
          }
          styleOverride={{
            color: colors.cgreen_600,
            width: "132px",
            padding: "8px 12px",
            fontWeight: "500",
            height: "36px",
          }}
        />
      </Box>
    </Box>
  );
}
